import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, Stethoscope, ClipboardList, Handshake, BadgeCheck } from "lucide-react";
import { Reveal } from "../components/Reveal";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import { PLACEMENTS, PARTNERS, SITE_URL } from "../data/siteContent";

const STEPS = [
  {
    icon: Stethoscope,
    n: "01",
    t: "Clinical postings",
    d: "From early semesters, students train on real hospital floors through our clinical partner hospitals in Bokaro.",
  },
  {
    icon: ClipboardList,
    n: "02",
    t: "Supervised internship",
    d: "Final-year internships place students inside partner hospitals under experienced preceptors and nursing supervisors.",
  },
  {
    icon: Handshake,
    n: "03",
    t: "Placement drives",
    d: "Our placement cell connects graduates directly with hiring hospitals and healthcare groups across India.",
  },
  {
    icon: BadgeCheck,
    n: "04",
    t: "Offer & onboarding",
    d: "Graduates receive placement offers with our partner network — backed by our 100% placement guarantee.",
  },
];

export default function Placements() {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL + "/" },
        { "@type": "ListItem", position: 2, name: "Placements", item: SITE_URL + "/placements" },
      ],
    },
  ];

  return (
    <div data-testid="placements-page">
      <SEO
        title="100% Placement Guarantee — Nursing Placements | SVS College of Nursing, Bokaro"
        description="SVS College of Nursing, Bokaro offers a 100% placement guarantee. Graduates are hired by Medanta, Manipal Hospitals, Sarvodaya Healthcare, Medicant and more partner hospitals."
        path="/placements"
        jsonLd={jsonLd}
      />

      <PageHero
        overline="Careers"
        title={<>Your career, <span className="italic text-gold-gradient">guaranteed</span></>}
        subtitle="Every SVS graduate steps into a nursing career — our placement cell works with leading hospitals so you never start your job hunt alone."
      />

      {/* Guarantee band */}
      <section className="bg-sand py-20" data-testid="guarantee-band">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="bg-navy text-sand rounded-[2px] p-10 lg:p-16 relative overflow-hidden" data-testid="placement-guarantee-card">
              <div className="grain-overlay" />
              <div className="relative grid lg:grid-cols-[auto,1fr] gap-10 items-center">
                <div className="flex items-center gap-6">
                  <div className="h-20 w-20 rounded-full bg-gold/15 ring-1 ring-gold/40 flex items-center justify-center shrink-0">
                    <CheckCircle2 size={40} className="text-gold" />
                  </div>
                  <div>
                    <div className="font-serif text-6xl text-gold-gradient">100%</div>
                    <div className="text-xs tracking-[0.25em] uppercase text-sand/70 mt-1">Placement Guaranteed</div>
                  </div>
                </div>
                <p className="text-sand/75 leading-relaxed max-w-2xl">
                  We back every enrolled student with a 100% placement guarantee. Through our
                  network of partner hospitals and a dedicated placement cell, SVS graduates
                  receive placement opportunities before they complete their programs — across
                  multi-specialty hospitals, community health and care organisations.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Placement partners */}
      <section className="bg-sand pb-24" data-testid="placement-partners">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="max-w-2xl mb-14">
              <span className="text-xs tracking-[0.3em] uppercase text-gold-muted font-mono">Recruiters</span>
              <h2 className="font-serif text-4xl sm:text-5xl text-navy mt-4">Where our graduates work</h2>
              <p className="text-navy/60 mt-5 leading-relaxed">
                Leading hospitals and healthcare groups that hire directly from SVS College of Nursing.
              </p>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {PLACEMENTS.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.07}>
                <motion.div
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.3 }}
                  className="group bg-white border border-navy/10 rounded-[2px] h-full flex flex-col items-center justify-center p-6 text-center"
                  data-testid={`placement-partner-${i}`}
                  title={p.name}
                >
                  <div className="h-24 w-full flex items-center justify-center">
                    <img
                      src={p.img}
                      alt={p.name}
                      loading="lazy"
                      className="max-h-24 max-w-full object-contain transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <p className="mt-4 text-xs font-medium text-navy/60 leading-snug">{p.name}</p>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white py-24" data-testid="placement-process">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="max-w-2xl mb-14">
              <span className="text-xs tracking-[0.3em] uppercase text-gold-muted font-mono">The Journey</span>
              <h2 className="font-serif text-4xl sm:text-5xl text-navy mt-4">How placement works</h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div className="bg-sand border border-navy/10 rounded-[2px] p-8 h-full" data-testid={`placement-step-${i}`}>
                  <div className="flex items-center justify-between mb-6">
                    <s.icon size={32} className="text-gold" strokeWidth={1.4} />
                    <span className="font-serif text-4xl text-gold/30">{s.n}</span>
                  </div>
                  <h3 className="font-serif text-xl text-navy mb-3">{s.t}</h3>
                  <p className="text-navy/60 text-sm leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Clinical partners */}
      <section className="bg-sand py-24" data-testid="clinical-partners-placements">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="max-w-2xl mb-14">
              <span className="text-xs tracking-[0.3em] uppercase text-gold-muted font-mono">Training Grounds</span>
              <h2 className="font-serif text-4xl sm:text-5xl text-navy mt-4">Our clinical partners</h2>
              <p className="text-navy/60 mt-5 leading-relaxed">
                Hospitals where SVS students complete supervised clinical postings — often the
                same hospitals that hire them after graduation.
              </p>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5">
            {PARTNERS.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.07}>
                <motion.div
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.3 }}
                  className="group bg-white border border-navy/10 rounded-[2px] h-full flex flex-col items-center justify-center p-6 text-center"
                  data-testid={`clinical-partner-${i}`}
                  title={p.name}
                >
                  <div className="h-24 w-full flex items-center justify-center">
                    <img
                      src={p.img}
                      alt={p.name}
                      loading="lazy"
                      className="max-h-24 max-w-full object-contain transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <p className="mt-4 text-xs font-medium text-navy/60 leading-snug">{p.name}</p>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy py-20 text-center relative overflow-hidden">
        <div className="grain-overlay" />
        <div className="relative max-w-2xl mx-auto px-5">
          <h2 className="font-serif text-3xl sm:text-4xl text-sand mb-6">
            Graduate with a job <span className="italic text-gold-gradient">waiting</span>
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/admissions"
              data-testid="placements-cta-apply"
              className="inline-flex items-center gap-2 rounded-full bg-gold text-navy font-bold px-8 py-4 transition-transform duration-300 hover:scale-105"
            >
              Apply for 2026-27 <ArrowUpRight size={18} />
            </Link>
            <Link
              to="/courses"
              data-testid="placements-cta-programs"
              className="inline-flex items-center gap-2 rounded-full border border-sand/40 text-sand font-semibold px-8 py-4 transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              Explore programs
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
